import React from 'react';
import { Chip } from '@jotpsych/ui';
import { Box, Typography } from '@mui/material';

import { useDisplayBundle } from '../bundleDisplay';
import type { BillingInterval, BundleWithStatus } from '../types';

interface TierTileProps {
  bundle: BundleWithStatus;
  billingInterval: BillingInterval;
  isSelected: boolean;
  /**
   * Marks this tier as the user's (or member's) currently-paid tier. Stays
   * anchored even when a different tile is selected — visual anchor for
   * comparison. Independent of `isSelected`. Mirrors `TierCard`.
   */
  isCurrent?: boolean;
  onSelect?: (bundleId: string) => void;
}

/**
 * Compact tier display for dense surfaces like the Person Card modal and the
 * upcoming Edit Plans batch modal. Strips marketing features, the Subscribe
 * button, and the big-typography pricing of `TierCard` down to a tile that
 * fits inside a constrained accordion or batch table cell.
 *
 * Shape (matches `prototypes/subscription-redesign/.../MembersTab.jsx`):
 *   - Tier name (with optional "Current" chip)
 *   - Monthly price
 *   - Whole tile is the click target
 */
export const TierTile: React.FC<TierTileProps> = ({
  bundle: rawBundle,
  billingInterval,
  isSelected,
  isCurrent = false,
  onSelect,
}) => {
  const bundle = useDisplayBundle(rawBundle) as BundleWithStatus;

  const priceCents =
    billingInterval === 'month'
      ? bundle.monthly_unit_amount
      : Math.round(bundle.yearly_unit_amount / 12);
  const formattedPrice = (priceCents / 100).toFixed(0);

  const handleClick = () => {
    if (onSelect) onSelect(bundle.bundle_id);
  };

  return (
    <Box
      data-testid={`tier-tile-${bundle.bundle_id}`}
      onClick={handleClick}
      role={onSelect ? 'button' : undefined}
      tabIndex={onSelect ? 0 : undefined}
      onKeyDown={e => {
        if (!onSelect) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      sx={{
        position: 'relative',
        p: 1.5,
        borderRadius: 1,
        border: '2px solid',
        borderColor: isSelected ? 'primary.main' : 'divider',
        cursor: onSelect ? 'pointer' : 'default',
        transition: theme => theme.transitions.create(['border-color']),
        '&:hover': onSelect ? { borderColor: 'primary.light' } : undefined,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {bundle.name}
        </Typography>
        {isCurrent && (
          <Chip label="Current" color="primary" variant="outlined" size="xSmall" />
        )}
      </Box>
      <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 700, mt: 0.5 }}>
        ${formattedPrice}
        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
          /mo
        </Typography>
      </Typography>
    </Box>
  );
};
